import {
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	EventEmitter,
	inject,
	Input,
	Output,
} from '@angular/core';

import { ClientScreenDirectives, MoveToRootModule } from '~app-client/core/directives';
import { OnboardingService } from '~app-client/onboarding/services';
import { OnboardingCondition } from '~app-client/onboarding/types';
import { UiNewButtons } from '~app-ui-new/buttons';

@Component({
	selector: 'base-modal',
	templateUrl: './base.component.html',
	styleUrls: ['./base.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: true,
	imports: [UiNewButtons, MoveToRootModule, ClientScreenDirectives],
	host: {
		class: 'dis-none',
	},
})
export class BaseModalComponent {
	private readonly onboardingService = inject(OnboardingService);
	private readonly cdRef = inject(ChangeDetectorRef);

	@Input()
	public showClose = false;

	@Input()
	public fullSize = false;

	@Input()
	public closeFromOutside = true;

	private _opened = false;

	@Input()
	public set opened(opened: boolean) {
		if (typeof opened !== typeof true) {
			return;
		}

		if (this._opened === opened) {
			return;
		}

		this._opened = opened;
		this.openedChanged$$.emit(this._opened);

		if (this._opened) {
			this.opened$$.emit();
			this.onboardingService.addCondition(OnboardingCondition.POPUP_OPENED);
		} else {
			this.closed$$.emit();
			this.onboardingService.removeCondition(OnboardingCondition.POPUP_OPENED);
		}

		this.cdRef.detectChanges();
	}

	public get opened(): boolean {
		return this._opened;
	}

	@Output('openedChanged')
	private readonly openedChanged$$ = new EventEmitter<boolean>();
	public readonly openedChanged$ = this.openedChanged$$.asObservable();

	@Output('closed')
	private readonly closed$$ = new EventEmitter<void>();
	public readonly closed$ = this.closed$$.asObservable();

	@Output('opened')
	private readonly opened$$ = new EventEmitter<void>();
	public readonly opened$ = this.opened$$.asObservable();

	public open(): void {
		this.opened = true;
	}

	public close(): void {
		this.opened = false;
	}

	public toggle(): void {
		this.opened = !this.opened;
	}

	public outsideClick(): void {
		if (this.closeFromOutside) {
			this.close();
		}
	}
}
